import React, { useRef, useState } from 'react';
import styles from './add-partial-payment-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ReactDatePicker from 'react-datepicker';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import 'react-dropzone-uploader/dist/styles.css';
import Dropzone from 'react-dropzone-uploader';
import Select, { ValueType } from 'react-select';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';

export interface AddPartialPaymentModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    paymentId?: string;
    currency?: string;
    totalAmount?: number;
  };

  eventHandlers: {
    closeHandler: () => void;
    addPartialPaymentHandler: (
      receipt: File,
      partialAmount: number,
      collectedAtDate: Date,
      paymentId?: string,
      skipEmail?: boolean,
      bankCharges?: number,
      bankChargesCurrency?: string,
      forexDifference?: number
    ) => void;
  };
}

/**
 * functional component AddPartialPaymentModal
 * @param {AddPartialPaymentModalProps}
 */
export const AddPartialPaymentModal: React.FC<AddPartialPaymentModalProps> = ({
  config,
  eventHandlers,
  data
}) => {
  const [newReceipt, setNewReceipt] = useState<File>();
  const [skipEmail, setSkipEmail] = useState<boolean>(false);
  const [collectedAtDate, setCollectedAtDate] = useState<Date>(new Date());
  const [partialAmount, setPartialAmount] = useState<number>(0);

  const [bankCharges, setBankChargesAmount] = useState<number | undefined>(undefined);
  const [bankChargesCurrency, setBankChargesCurrency] = useState<string | undefined>(undefined);
  const [forexDifference, setForexDifferences] = useState<number | undefined>(undefined);

  const inputIdPrefixRef = useRef(`partial-payment-${Math.random().toString(36).slice(2)}`);
  const partialAmountId = `${inputIdPrefixRef.current}-partial-amount`;
  const bankChargesId = `${inputIdPrefixRef.current}-bank-charges-amount`;
  const bankChargesCurrencyId = `${inputIdPrefixRef.current}-bank-charges-currency`;
  const forexDifferenceId = `${inputIdPrefixRef.current}-forex-differences`;

  const isMobileView = useWindowResizer();

  const handleAddPartialPaymentSubmit = () => {
    if (newReceipt && partialAmount > 0 && data.paymentId) {
      eventHandlers.addPartialPaymentHandler(
        newReceipt,
        partialAmount,
        collectedAtDate,
        data.paymentId,
        skipEmail,
        bankCharges,
        bankChargesCurrency,
        forexDifference
      );
    }
  };

  const handleSkipEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkipEmail(event.target.checked);
  };

  const handlePartialAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value) || 0;
    if (data.totalAmount && value <= data.totalAmount) {
      setPartialAmount(value);
    } else if (!data.totalAmount) {
      setPartialAmount(value);
    }
  };

  const isValidAmount =
    partialAmount > 0 && (!data.totalAmount || partialAmount <= data.totalAmount);

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['add-partial-payment-modal']}>
        <div className={styles['add-partial-payment-modal__title']}>
          Add Partial Payment Receipt
        </div>

        <div className={styles['add-partial-payment-modal__description']}>
          <div className={styles['add-partial-payment-modal__amount-container']}>
            <label htmlFor={partialAmountId}>Partial Payment Amount:</label>
            <div className={styles['add-partial-payment-modal__amount-input-wrapper']}>
              <span className={styles['add-partial-payment-modal__currency']}>
                {data.currency && (data.currency === 'usd' || data.currency === 'egp')
                  ? CURRENCY[data.currency]
                  : 'EGP'}
              </span>
              <input
                id={partialAmountId}
                type="number"
                value={partialAmount}
                onChange={handlePartialAmountChange}
                placeholder="Enter amount"
                min="0"
                className={styles['add-partial-payment-modal__amount-input']}
              />
            </div>
            {data.totalAmount && (
              <small className={styles['add-partial-payment-modal__helper-text']}>
                Total amount:{' '}
                {data.currency && (data.currency === 'usd' || data.currency === 'egp')
                  ? CURRENCY[data.currency]
                  : 'EGP'}{' '}
                {data.totalAmount}
              </small>
            )}
          </div>
        </div>

        <div className={styles['add-partial-payment-modal__description']}>
          Collected At:
          <ReactDatePicker
            selected={collectedAtDate}
            onChange={(date) => setCollectedAtDate(date as Date)}
          />
        </div>

        <div className={styles['add-partial-payment-modal__description']}>
          <div className={styles['add-partial-payment-modal__form-grid']}>
            <label htmlFor={bankChargesId}>
              Bank Charges Amount{' '}
              <small className={styles['add-partial-payment-modal__optional']}>(optional)</small>
            </label>
            <div className={styles['add-partial-payment-modal__field-row']}>
              <div className={styles['add-partial-payment-modal__field']}>
                <InputText
                  data={{
                    value: bankCharges ?? '',
                    placeholder: 'Enter amount'
                  }}
                  config={{
                    type: InputTextType.number
                  }}
                  eventHandlers={{
                    onChangeHandler: (value) => {
                      const numericValue = parseFloat(value);
                      setBankChargesAmount(
                        Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined
                      );
                      if (!value) {
                        setBankChargesCurrency(undefined);
                      }
                    }
                  }}
                />
              </div>
              <div className={styles['add-partial-payment-modal__field']}>
                <Select
                  inputId={bankChargesCurrencyId}
                  value={
                    bankChargesCurrency
                      ? {
                          value: bankChargesCurrency,
                          label: CURRENCY[bankChargesCurrency as keyof typeof CURRENCY]
                        }
                      : null
                  }
                  onChange={(option: ValueType<{ value: string; label: string }, false>) => {
                    const selected = option as { value: string; label: string } | null;
                    setBankChargesCurrency(selected?.value ?? undefined);
                  }}
                  options={[
                    { value: 'egp', label: CURRENCY.egp },
                    { value: 'usd', label: CURRENCY.usd }
                  ]}
                  isDisabled={!bankCharges || bankCharges <= 0}
                  styles={{
                    control: (provided) =>
                      ({
                        ...provided,
                        minHeight: '46px',
                        height: '46px'
                      } as any),
                    valueContainer: (provided) =>
                      ({
                        ...provided,
                        height: '46px'
                      } as any),
                    singleValue: (provided) =>
                      ({
                        ...provided,
                        height: '46px'
                      } as any)
                  }}
                />
              </div>
            </div>

            <label htmlFor={forexDifferenceId}>
              Forex Differences{' '}
              <small className={styles['add-partial-payment-modal__optional']}>(optional)</small>
            </label>
            <div className={styles['add-partial-payment-modal__field']}>
              <InputText
                data={{
                  value: forexDifference ?? '',
                  placeholder: 'Enter amount'
                }}
                config={{
                  type: InputTextType.number
                }}
                eventHandlers={{
                  onChangeHandler: (value) => {
                    const numericValue = parseFloat(value);
                    setForexDifferences(
                      Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined
                    );
                  }
                }}
              />
            </div>
          </div>
        </div>

        <Dropzone
          classNames={{ input: styles['add-partial-payment-modal__dropzone'] }}
          inputContent={() => (
            <div>
              <CloudUploadIcon fontSize={'large'} />
              Upload proof of payment
            </div>
          )}
          accept="*"
          maxFiles={1}
          multiple={false}
          onChangeStatus={(file) => {
            setNewReceipt(file.file);
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={skipEmail}
              onChange={handleSkipEmailChange}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          }
          label="Do NOT send email to guest"
        />

        <div className={styles['add-partial-payment-modal--actions']}>
          <Button
            onClick={() => {
              eventHandlers.closeHandler();
            }}
            type={ButtonType.primaryInverted}
            tracking={{ event: 'record_edit_cancelled', entity: 'payment', method: 'close' }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            disabled={!newReceipt || !isValidAmount}
            onClick={handleAddPartialPaymentSubmit}
            tracking={{
              event: 'record_saved',
              entity: 'payment',
              action: 'update',
              outcome: 'success'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Add Partial Payment
          </Button>
        </div>
      </div>
    </Modal>
  );
};
